import type { CopyRenderer, CopySegment } from './types'

/**
 * Sambung Sekolah-Rumah copy.
 *
 * Two audiences with opposite viewpoints: the school sees its own request and what became of it, the
 * parent sees an incoming ask. `actorName` is the teacher on every one of these events, including the
 * outcomes — a rejection is still attributed to the request the teacher made.
 */

const UNKNOWN_STUDENT = 'Siswa'
const UNKNOWN_TEACHER = 'Seorang guru'
const UNKNOWN_SCHOOL = 'sekolahnya'
const UNKNOWN_PARENT = 'Orang tua'

/** "Permohonan sambung sekolah-rumah untuk {Siswa} terkirim ke {email}." */
export const renderConnectRequestedActorCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const parentEmail = view.snapshot.parentEmail

  const body: CopySegment[] = [{ text: 'Permohonan sambung sekolah-rumah untuk ' }, { text: studentName, bold: true }]

  // The address is the whole point of the receipt — but a resend that lost its snapshot should still
  // render a sentence rather than trailing off into "terkirim ke ".
  if (parentEmail) {
    body.push({ text: ' terkirim ke ' }, { text: parentEmail, bold: true }, { text: '.' })
  } else {
    body.push({ text: ' berhasil terkirim.' })
  }

  return { body }
}

/** "{Guru} meminta sambung sekolah-rumah untuk {Siswa} dengan {Sekolah}" */
export const renderConnectRequestedRecipientCopy: CopyRenderer = (view) => {
  const teacherName = view.snapshot.actorName ?? UNKNOWN_TEACHER
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const schoolName = view.snapshot.schoolName ?? UNKNOWN_SCHOOL

  return {
    body: [
      { text: teacherName, bold: true },
      { text: ' meminta sambung sekolah-rumah untuk ' },
      { text: studentName, bold: true },
      { text: ' dengan ' },
      { text: schoolName, bold: true },
    ],
  }
}

/** "{Siswa} berhasil tersambung dengan akun orang tuanya." */
export const renderConnectAcceptedRecipientCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT

  return {
    body: [{ text: studentName, bold: true }, { text: ' berhasil tersambung dengan akun orang tuanya.' }],
  }
}

/**
 * "{Siswa} gagal tersambung dengan akun orang tuanya. Permohonan ditolak oleh {email} dengan alasan:
 * {"Alasan"}"
 */
export const renderConnectRejectedRecipientCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const parentEmail = view.snapshot.parentEmail
  const reason = view.snapshot.rejectionReason?.trim()

  const body: CopySegment[] = [
    { text: studentName, bold: true },
    { text: ' gagal tersambung dengan akun orang tuanya.' },
  ]

  if (parentEmail) {
    body.push({ text: ' Permohonan ditolak oleh ' }, { text: parentEmail, bold: true })
  } else {
    body.push({ text: ' Permohonan ditolak' })
  }

  // Declining without a reason is allowed, so the sentence has to close cleanly either way.
  if (reason) {
    body.push({ text: ' dengan alasan: ' }, { text: `“${reason}”` })
  } else {
    body.push({ text: '.' })
  }

  return { body }
}

/**
 * "{Guru} mengirim pesan tentang {Siswa}" plus the teacher's own words underneath.
 *
 * The four templates in the dialog only prefill the textarea, so there is exactly one renderer here
 * rather than one per template — the server never learns which was picked, and the text may have
 * been edited past recognition anyway.
 */
export const renderConnectParentMessageRecipientCopy: CopyRenderer = (view) => {
  const teacherName = view.snapshot.actorName ?? UNKNOWN_TEACHER
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const message = view.snapshot.message?.trim()

  return {
    body: [{ text: teacherName, bold: true }, { text: ' mengirim pesan tentang ' }, { text: studentName, bold: true }],
    // An empty message is rejected at the router, so this only guards rows written before the
    // field existed — the sentence above still reads correctly on its own.
    quotedLine: message ? `“${message}”` : undefined,
  }
}

/**
 * Parent-side identity edits on a dual-connected child (Sambung Sekolah-Rumah). `actorName` is the
 * parent here, not the teacher — the only other renderers in this file where that's true.
 */

/**
 * "PIN Pelajar milik {Siswa} baru saja diperbarui oleh orang tuanya" — to the school's
 * owner/admin/guru kelas.
 *
 * Leads with the student, not the actor: a teacher scanning the feed is looking for which of their
 * students changed, and the parent's name is the part they cannot act on. "Orang tuanya" rather
 * than the parent's name for the same reason — the relationship is what makes the edit legitimate.
 */
export const renderConnectChildPinUpdatedRecipientCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT

  return {
    body: [
      { text: 'PIN Pelajar', bold: true },
      { text: ' milik ' },
      { text: studentName, bold: true },
      { text: ' baru saja diperbarui oleh orang tuanya' },
    ],
  }
}

/** "PIN Pelajar milik {Siswa} telah kamu perbarui" — the parent's own receipt. */
export const renderConnectChildPinUpdatedActorCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT

  return {
    body: [
      { text: 'PIN Pelajar', bold: true },
      { text: ' milik ' },
      { text: studentName, bold: true },
      { text: ' telah kamu perbarui' },
    ],
  }
}

/** "{Orang tua} memperbarui nama anaknya {Siswa}" — to the school's owner/admin/guru kelas. */
export const renderConnectChildNameUpdatedRecipientCopy: CopyRenderer = (view) => {
  const parentName = view.snapshot.actorName ?? UNKNOWN_PARENT
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT

  return {
    body: [
      { text: parentName, bold: true },
      { text: ' memperbarui ' },
      { text: 'nama', bold: true },
      { text: ' anaknya ' },
      { text: studentName, bold: true },
    ],
  }
}

/** "Anda memperbarui nama anaknya {Siswa}" — the parent's own receipt. */
export const renderConnectChildNameUpdatedActorCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT

  return {
    body: [
      { text: 'Anda memperbarui ' },
      { text: 'nama', bold: true },
      { text: ' anaknya ' },
      { text: studentName, bold: true },
    ],
  }
}

/** "{Siswa} telah dikeluarkan dari {Sekolah}" */
export const renderConnectStudentRemovedRecipientCopy: CopyRenderer = (view) => {
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const schoolName = view.snapshot.schoolName ?? UNKNOWN_SCHOOL

  return {
    body: [{ text: studentName, bold: true }, { text: ' telah dikeluarkan dari ' }, { text: schoolName, bold: true }],
  }
}

/**
 * "{Orang tua} memohon agar {Siswa} dikeluarkan dari {Sekolah}" plus the parent's own words.
 *
 * `actorName` is the parent here, not a teacher — this is the one event in this file that travels
 * from the family towards the school. The message is optional (the dialog prefills the textarea but
 * lets it be cleared), so the sentence has to close cleanly with no `quotedLine` at all.
 */
export const renderConnectRemovalRequestedRecipientCopy: CopyRenderer = (view) => {
  const parentName = view.snapshot.actorName ?? UNKNOWN_PARENT
  const studentName = view.snapshot.studentName ?? UNKNOWN_STUDENT
  const schoolName = view.snapshot.schoolName ?? UNKNOWN_SCHOOL
  const message = view.snapshot.message?.trim()

  return {
    body: [
      { text: parentName, bold: true },
      { text: ' memohon agar ' },
      { text: studentName, bold: true },
      { text: ' dikeluarkan dari ' },
      { text: schoolName, bold: true },
    ],
    quotedLine: message ? `“${message}”` : undefined,
  }
}
